import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { productService } from '../services/api';

function getStatus(p) {
  if (p.status) return p.status;
  if (!p.active) return 'offline';
  if (p.stock === 0) return 'maintenance';
  return 'operational';
}

function StatusBadge({ status }) {
  const map = {
    operational: { cls: 'sop', label: 'Operational', pulse: true },
    maintenance: { cls: 'smt', label: 'Maintenance',  pulse: false },
    degraded:    { cls: 'sdg', label: 'Degraded',     pulse: false },
    offline:     { cls: 'sof', label: 'Offline',      pulse: false },
  };
  const { cls, label, pulse } = map[status] || map.operational;
  return (
    <span className={`status-badge ${cls}`}>
      <span className={`sbadge-dot${pulse ? ' sbadge-pulse' : ''}`}></span>
      {label}
    </span>
  );
}

const StatusPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [counts, setCounts]     = useState({ op: 0, mt: 0, dg: 0, of: 0 });
  const [countdown, setCountdown] = useState(60);
  const [overall, setOverall]   = useState('all-ok'); // 'all-ok' | 'maintenance' | 'critical'

  const loadStatus = useCallback(async () => {
    try {
      const response = await productService.getAll();
      const data = response.data.products || [];

      const rows = [];
      let op = 0, mt = 0, dg = 0, of = 0;

      data.forEach(p => {
        const st = getStatus(p);
        rows.push({ ...p, _status: st });
        if (st === 'operational') op++;
        else if (st === 'maintenance') mt++;
        else if (st === 'degraded') dg++;
        else of++;
      });

      setProducts(rows);
      setCounts({ op, mt, dg, of });
      setOverall(of > 0 || dg > 0 ? 'critical' : mt > 0 ? 'maintenance' : 'all-ok');
      setLoading(false);
    } catch {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  // Countdown + auto-refresh
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(s => {
        if (s <= 1) {
          loadStatus();
          return 60;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [loadStatus]);

  const bannerConfig = {
    'all-ok':      { bg: 'rgba(34,197,94,0.1)',    border: 'rgba(34,197,94,0.28)',    dot: '#22c55e', label: 'All Systems Operational',        labelColor: '#22c55e' },
    'maintenance': { bg: 'rgba(245,158,11,0.1)',   border: 'rgba(245,158,11,0.28)',   dot: '#f59e0b', label: 'Some Systems Under Maintenance',  labelColor: '#f59e0b' },
    'critical':    { bg: 'rgba(239,68,68,0.1)',    border: 'rgba(239,68,68,0.28)',    dot: '#ef4444', label: 'Some Systems Are Down',           labelColor: '#ef4444' },
  };
  const bc = bannerConfig[overall];
  const today = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  return (
    <div className="status-page-standalone">

      {/* Hero */}
      <div className="status-hero">
        <div className="status-hero-badge">
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--accent)', display: 'inline-block', animation: 'blink 2s ease-in-out infinite' }}></span>
          Live System Status
        </div>
        <h1>IWantCheats Status</h1>
        <p>Real-time status for all products</p>
      </div>

      {/* Banner */}
      <div
        className="status-banner"
        style={{ background: bc.bg, borderColor: bc.border }}
      >
        <div className="status-banner-left">
          <div className="status-banner-dot" style={{ background: bc.dot, boxShadow: `${bc.dot} 0 0 8px` }}></div>
          <div>
            <div className="status-banner-label" style={{ color: bc.labelColor }}>{bc.label}</div>
            <div className="status-banner-sub">Updates in {countdown}s</div>
          </div>
        </div>
        <div className="status-stats">
          <div className="status-stat">
            <div className="status-stat-number" style={{ color: '#22c55e' }}>{counts.op}</div>
            <div className="status-stat-label">Operational</div>
          </div>
          <div className="status-stat">
            <div className="status-stat-number" style={{ color: '#f59e0b' }}>{counts.mt}</div>
            <div className="status-stat-label">Maintenance</div>
          </div>
          <div className="status-stat">
            <div className="status-stat-number" style={{ color: '#fb923c' }}>{counts.dg}</div>
            <div className="status-stat-label">Degraded</div>
          </div>
          <div className="status-stat">
            <div className="status-stat-number" style={{ color: '#ef4444' }}>{counts.of}</div>
            <div className="status-stat-label">Offline</div>
          </div>
        </div>
      </div>

      {/* Ref */}
      <div className="status-ref">
        Auto-refreshing in <span>{countdown}s</span> &nbsp;·&nbsp; <a href="#!" onClick={e => { e.preventDefault(); loadStatus(); setCountdown(60); }}>Refresh now</a>
      </div>

      {/* Legend */}
      <div className="status-legend">
        <div className="status-leg-item"><span className="status-leg-dot" style={{ background: '#22c55e', boxShadow: '#22c55e 0 0 5px' }}></span>Operational</div>
        <div className="status-leg-item"><span className="status-leg-dot" style={{ background: '#f59e0b' }}></span>Maintenance</div>
        <div className="status-leg-item"><span className="status-leg-dot" style={{ background: '#fb923c' }}></span>Degraded</div>
        <div className="status-leg-item"><span className="status-leg-dot" style={{ background: '#ef4444' }}></span>Offline</div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="loading" style={{ padding: '40px 0' }}>
          <p style={{ color: 'var(--muted)', fontSize: 14 }}>Loading product status...</p>
        </div>
      )}

      {/* Product List */}
      {!loading && (
        <div className="status-list">
          <div className="status-list-header">
            <span>Product</span>
            <span>Status</span>
          </div>
          {products.map(p => {
            return (
              <div className="status-row" key={p.id}>
                <div className="status-row-name">
                  <span className="status-row-title">{p.name}</span>
                </div>
                <StatusBadge status={p._status} />
              </div>
            );
          })}

          {products.length === 0 && (
            <div className="status-empty">
              No products found. <Link to="/" style={{ color: 'var(--accent)' }}>Return home</Link>
            </div>
          )}
        </div>
      )}

      {/* Empty fallback */}
    </div>
  );
};

export default StatusPage;
